// @ts-nocheck
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './CurrencySelector.css';

const symbols = { USD: '$', CAD: 'CA$', EUR: '€', AUD: 'A$', GBP: '£' };
const currencyCode = (value) =>
  typeof value === 'string' && /^[a-z]{3}$/i.test(value)
    ? value.toUpperCase()
    : '';

/**
 * Controlled payment-currency presentation. No requests, conversions or PI state.
 * selectedCurrency is the parent's confirmed backend response (currency,
 * chargeAmount in major units, expiresAt in Unix seconds), or null for the
 * operator currency. Availability is discovery only; its expiry cannot lock a PI.
 * The parent must block payment while loading, expired/invalidated or awaiting
 * reconfirmation, and preserve the confirmed amount until replacement succeeds.
 */
const CurrencySelector = ({
  operatorCurrency,
  availability,
  selectedCurrency = null,
  preferredCurrency = null,
  showWhenUnavailable = false,
  loading = false,
  error = null,
  disabled = false,
  invalidated = false,
  multiCurrencyEnabled,
  showLoadingText = true,
  showRateLockMessage = true,
  onCurrencyChange,
  onRefresh
}) => {
  const { t, i18n } = useTranslation('bookings');
  const flags = useFlags();
  const enabled = multiCurrencyEnabled ?? flags.enableMultiCurrency ?? false;
  const base = currencyCode(operatorCurrency);
  const confirmed = currencyCode(selectedCurrency?.currency);
  const selected = currencyCode(preferredCurrency) || confirmed || base;
  const foreign = Boolean(selected && base && selected !== base);
  const hasForeignSelection = Boolean(confirmed && base && confirmed !== base);
  const availabilityReady = Boolean(
    availability && currencyCode(availability.operatorCurrency) === base
  );
  const currencies = [
    base,
    ...Object.entries(availabilityReady ? availability.rates || {} : {})
      .filter(([, value]) => Number.isFinite(value?.rate) && value.rate > 0)
      .map(([code]) => currencyCode(code)),
    ...(foreign ? [selected] : [])
  ].filter((code, index, values) => code && values.indexOf(code) === index);
  const expiresAt = selected === confirmed ? selectedCurrency?.expiresAt : null;
  const hasExpiry = Number.isFinite(expiresAt) && expiresAt > 0;
  const [now, setNow] = useState(() => Date.now() / 1000);
  const expired = foreign && hasExpiry && now >= expiresAt;
  const expiring = foreign && hasExpiry && now > expiresAt - 60;
  const needsRefresh = foreign && (expired || invalidated);
  const blocked = disabled || loading;
  const unavailable = !enabled || Boolean(error) || !availabilityReady;
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const root = useRef(null);
  const trigger = useRef(null);
  const options = useRef([]);
  const id = useId();

  useEffect(() => {
    const updateTime = () => setNow(Date.now() / 1000);
    updateTime();
    window.addEventListener('focus', updateTime);
    document.addEventListener('visibilitychange', updateTime);
    return () => {
      window.removeEventListener('focus', updateTime);
      document.removeEventListener('visibilitychange', updateTime);
    };
  }, [expiresAt, foreign]);

  useEffect(() => {
    const nextBoundary = now <= expiresAt - 60 ? expiresAt - 60 : expiresAt;
    const timer =
      foreign && hasExpiry && now < expiresAt
        ? setTimeout(
            () => setNow(Date.now() / 1000),
            Math.min(2147483647, Math.max(1, (nextBoundary - now) * 1000 + 1))
          )
        : null;
    return () => clearTimeout(timer);
  }, [expiresAt, foreign, hasExpiry, now]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  useEffect(() => {
    if (blocked) setOpen(false);
  }, [blocked]);

  useEffect(() => {
    if (open) options.current[focusedIndex]?.focus();
  }, [open, focusedIndex]);

  // Initial discovery failures hide the control. Active FX state must remain
  // visible even after flag/network failures; no implicit currency fallback.
  if (
    !base ||
    (!enabled && !hasForeignSelection) ||
    (!foreign &&
      (!enabled ||
        (!showWhenUnavailable && (error || (!availabilityReady && !loading)))))
  )
    return null;

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const canSelect = (code) =>
    !blocked && (code === base || (enabled && !unavailable));
  const openOptions = () => {
    if (blocked || (!foreign && !availabilityReady)) return;
    setFocusedIndex(Math.max(0, currencies.indexOf(selected)));
    setOpen(true);
  };
  const handleOptionKey = (event, index) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? currencies.length - 1
            : (index +
                (event.key === 'ArrowDown' ? 1 : -1) +
                currencies.length) %
              currencies.length;
      setFocusedIndex(next);
    }
  };
  const amount = selected === confirmed ? selectedCurrency?.chargeAmount : null;
  const validatedAmount =
    typeof amount === 'number' && Number.isFinite(amount) && amount >= 0
      ? new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || 'en', {
          style: 'currency',
          currency: selected
        }).format(amount)
      : null;
  const status = loading
    ? t('currencySelector.loading')
    : expired
      ? t('currencySelector.expired')
      : needsRefresh
        ? t('currencySelector.quoteInvalidated')
        : unavailable && foreign
          ? t('currencySelector.unavailable')
          : expiring
            ? showRateLockMessage
              ? t('currencySelector.expiring')
              : ''
            : foreign && hasExpiry
              ? showRateLockMessage
                ? t('currencySelector.locked')
                : ''
              : '';

  return (
    <div
      className='currency-selector'
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type='button'
        className={`currency-selector__trigger${foreign ? ' currency-selector__trigger--foreign' : ''}`}
        aria-label={t('currencySelector.select', { currency: selected })}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={open ? `${id}-options` : undefined}
        aria-describedby={
          status || validatedAmount ? `${id}-status` : undefined
        }
        aria-busy={loading}
        disabled={blocked || (!foreign && !availabilityReady)}
        onClick={() => (open ? close() : openOptions())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openOptions();
          } else if (event.key === 'Escape') close();
        }}
      >
        <span>{selected}</span>
        {loading ? (
          <span className='currency-selector__spinner' aria-hidden='true' />
        ) : (
          <span className='currency-selector__chevron' aria-hidden='true'>
            ▾
          </span>
        )}
      </button>
      {open && (
        <ul
          id={`${id}-options`}
          role='listbox'
          aria-label={t('currencySelector.currencies')}
          className='currency-selector__options'
        >
          {currencies.map((code, index) => (
            <li key={code} role='none'>
              <button
                ref={(element) => (options.current[index] = element)}
                type='button'
                role='option'
                aria-selected={code === selected}
                aria-disabled={!canSelect(code)}
                tabIndex={focusedIndex === index ? 0 : -1}
                className='currency-selector__option'
                onKeyDown={(event) => handleOptionKey(event, index)}
                onClick={() => {
                  if (!canSelect(code)) return;
                  if (code !== selected) onCurrencyChange?.(code);
                  close();
                }}
              >
                <span className='currency-selector__symbol' aria-hidden='true'>
                  {symbols[code] || code}
                </span>
                <span>{code}</span>
                <span aria-hidden='true'>{code === selected ? '✓' : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div
        id={`${id}-status`}
        role='status'
        className={`currency-selector__status${needsRefresh || expiring ? ' currency-selector__status--warning' : ''}`}
      >
        {status && (
          <span
            className={
              loading && !showLoadingText
                ? 'currency-selector__sr-only'
                : undefined
            }
          >
            {status}
          </span>
        )}
        {validatedAmount && foreign && (
          <span className='currency-selector__sr-only'>
            {t('currencySelector.confirmedAmount', {
              amount: validatedAmount,
              currency: selected
            })}
          </span>
        )}
        {needsRefresh && !loading && (
          <button
            type='button'
            className='currency-selector__refresh'
            disabled={blocked || !enabled}
            onClick={() => onRefresh?.()}
          >
            {t('currencySelector.refresh')}
          </button>
        )}
      </div>
    </div>
  );
};

export default CurrencySelector;
